<template>
    <div class="compliance-information-v2">
        <b-row v-if="settings.showSummary !== false" class="compliance-information-v2__summary">
            <b-col cols="12" md="4">
                <RequirementSummaryCard
                    :count="completedCount"
                    :label="translate(
                        'Page(ReceivedRequest):Component(ComplianceInformation):requirements-completed',
                        'requirements completed',
                    )"
                    icon="CheckSquareIcon"
                    icon-class="text-success"
                    :explanation-title="translate(
                        'Page(ReceivedRequest):Component(ComplianceInformation):completed-explanation-title',
                        'Completed requirements',
                    )"
                    :explanations="completedExplanations"
                />
            </b-col>
            <b-col cols="12" md="4">
                <RequirementSummaryCard
                    :count="needsInputCount"
                    :label="translate(
                        'Page(ReceivedRequest):Component(ComplianceInformation):requirements-need-input',
                        'requirements need your input',
                    )"
                    icon="HelpCircleIcon"
                    icon-class="text-primary"
                    :explanation-title="translate(
                        'Page(ReceivedRequest):Component(ComplianceInformation):required-explanation-title',
                        'Information still required',
                    )"
                    :explanations="needsInputExplanations"
                />
            </b-col>
            <b-col cols="12" md="4">
                <RequirementSummaryCard
                    :count="substanceCount"
                    :label="translate(
                        'Page(ReceivedRequest):Component(ComplianceInformation):substances-identified',
                        'substances identified',
                    )"
                    icon="HexagonIcon"
                    icon-class="text-primary"
                />
            </b-col>
        </b-row>

        <ComplexProductComplianceOverview
            v-if="complex"
            :requirements="requirements"
            :context="context"
            :property-config="propertyConfig"
            :loaded="loaded"
            :substance-component="settings.substanceComponent"
            :show-requirements-heading="showRequirementsHeading"
            :show-substance-requirement-status="showSubstanceRequirementStatus"
            @action="$emit('action', $event)"
        />
        <SimpleProductComplianceOverview
            v-else
            :requirements="requirements"
            :context="context"
            :property-config="propertyConfig"
            :loaded="loaded"
            :requirement-display="simpleRequirementDisplay"
            :show-requirements-heading="showRequirementsHeading"
            :show-substance-requirement-status="showSubstanceRequirementStatus"
            @action="$emit('action', $event)"
            @scip-input="handleScipInput"
        />
    </div>
</template>

<script>
import { BCol, BRow } from 'bootstrap-vue'
import {
    isComplexProduct,
    uniqueSubstanceCount,
} from './compliance-requirements'
import {
    buildComplianceInformationV2Requirements,
    createComplianceInformationV2Validation,
} from './compliance-information-v2-validation'
import {
    blockingReasons,
    completedReasons,
} from './requirement-checks'
import ComplexProductComplianceOverview from './components/ComplexProductComplianceOverview.vue'
import RequirementSummaryCard from './components/RequirementSummaryCard.vue'
import SimpleProductComplianceOverview from './components/SimpleProductComplianceOverview.vue'

export default {
    name: 'ComplianceInformationStepV2',
    components: {
        BCol,
        BRow,
        ComplexProductComplianceOverview,
        RequirementSummaryCard,
        SimpleProductComplianceOverview,
    },
    props: {
        context: { type: Object, required: true },
        settings: { type: Object, required: true },
        runtimeState: { type: Object, required: true },
    },
    computed: {
        complianceState() {
            return this.context.complianceInformation || {
                substanceLists: [],
                propertyConfig: { map: { list: {} }, properties: {} },
                loaded: false,
            }
        },
        loaded() {
            return this.complianceState.loaded === true
        },
        propertyConfig() {
            return this.complianceState.propertyConfig
        },
        requirements() {
            return buildComplianceInformationV2Requirements(this.context, this.settings)
        },
        completedCount() {
            return this.requirements.filter(requirement => requirement.status === 'complete').length
        },
        needsInputCount() {
            return this.requirements.length - this.completedCount
        },
        substanceCount() {
            return uniqueSubstanceCount(this.requirements)
        },
        completedExplanations() {
            return this.requirementExplanationGroups('complete', completedReasons)
        },
        needsInputExplanations() {
            return this.requirementExplanationGroups('needs-input', blockingReasons)
        },
        complex() {
            return isComplexProduct(this.context)
        },
        simpleRequirementDisplay() {
            return this.settings.simpleOverview
                ? this.settings.simpleOverview.requirementDisplay
                : 'expanded'
        },
        showSubstanceRequirementStatus() {
            return this.settings.showSubstanceRequirementStatus !== false
        },
        showRequirementsHeading() {
            return this.settings.showRequirementsHeading === true
        },
        validationResult() {
            return createComplianceInformationV2Validation(
                this.context,
                this.settings,
                this.requirements,
            )
        },
        inferredAnswerSignature() {
            return this.requirements
                .reduce((ids, requirement) => ids.concat(
                    (requirement.inferredQuestions || []).map(question => question.id),
                ), [])
                .join(',')
        },
    },
    watch: {
        validationResult: {
            deep: true,
            immediate: true,
            handler(result) {
                this.$emit('validity-change', result)
            },
        },
        inferredAnswerSignature: {
            immediate: true,
            handler() {
                this.synchronizeInferredAnswers()
            },
        },
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        requirementExplanationGroups(status, messageResolver) {
            return this.requirements
                .filter(requirement => requirement.status === status)
                .map(requirement => ({
                    id: requirement.id,
                    title: requirement.list.title,
                    legislations: requirement.legislations
                        .map(legislation => legislation.title || legislation.name || legislation.key)
                        .filter(Boolean),
                    messages: messageResolver(requirement.checks),
                }))
                .filter(group => group.messages.length > 0)
        },
        handleScipInput(value) {
            this.$emit('action', {
                type: 'scip-number.change',
                payload: { value },
            })
        },
        synchronizeInferredAnswers() {
            this.requirements
                .forEach(requirement => {
                    (requirement.inferredQuestions || []).forEach(question => {
                        this.$emit('action', {
                            type: 'list-question.update',
                            payload: { list: question.list, state: true },
                        })
                    })
                })
        },
    },
}
</script>

<style lang="scss" scoped>
.compliance-information-v2 {
    &__summary {
        margin-bottom: 1.5rem;
    }

}

@media (max-width: 767.98px) {
    .compliance-information-v2__summary-card {
        margin-bottom: .75rem;
    }
}
</style>
